import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import useIsMobile from '../hooks/useIsMobile';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function Onboarding() {
  const navigate = useNavigate();
  const { getToken, user } = useAuth();
  const isMobile = useIsMobile();

  const [step, setStep]                         = useState(1);
  const [form, setForm]                         = useState({
    currentTitle: '', currentCompany: '',
    targetTitle: '', targetCompany: '',
    targetJD: '',
  });
  const [resumeFile, setResumeFile]             = useState(null);
  const [jdFile, setJdFile]                     = useState(null);
  const [jdMode, setJdMode]                     = useState('paste');
  const [error, setError]                       = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── File size validator ───────────────────────────────────────────────────
  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setError('Resume file is too large. Please upload a file under 10MB.');
      e.target.value = '';
      return;
    }
    setError('');
    setResumeFile(file);
  };

  const handleJdFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setError('JD file is too large. Please upload a file under 10MB.');
      e.target.value = '';
      return;
    }
    setError('');
    setJdFile(file);
  };

  // Check sessionStorage for upgrade modal flag on mount
  useEffect(() => {
    if (sessionStorage.getItem('showUpgradeModal') === 'true') {
      sessionStorage.removeItem('showUpgradeModal');
      setShowUpgradeModal(true);
    }
  }, []);

  // Check plan on page load
  useEffect(() => {
    async function checkPlan() {
      try {
        const token = await getToken();
        const res = await axios.get(
          process.env.REACT_APP_API_URL + '/api/payments/my-plan',
          { headers: { Authorization: 'Bearer ' + token } }
        );
        const plan = res.data;
        if (
          plan &&
          plan.free_analysis_used &&
          plan.resume_analyses_remaining <= 0 &&
          plan.interview_analyses_remaining <= 0
        ) {
          setShowUpgradeModal(true);
        }
      } catch (err) {
        console.log('Plan check failed:', err.message);
      }
    }
    checkPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enter key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (step < 4) {
          setStep(s => s + 1);
        } else {
          handleSubmit();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form, resumeFile, jdFile, jdMode]);

  // Handle payment from modal
  const handleModalPurchase = async (planType) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(
        process.env.REACT_APP_API_URL + '/api/payments/create-order',
        { planType },
        { headers: { Authorization: 'Bearer ' + token } }
      );

      const options = {
        key:         data.keyId,
        amount:      data.amount,
        currency:    'INR',
        order_id:    data.orderId,
        name:        'CrackPrep',
        description: data.planName,
        theme:       { color: '#00D4AA' },
        handler: async function(response) {
          try {
            await axios.post(
              process.env.REACT_APP_API_URL + '/api/payments/verify-payment',
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                planType,
              },
              { headers: { Authorization: 'Bearer ' + token } }
            );
            setShowUpgradeModal(false);
            alert('Payment successful! You can now run your analysis. 🎉');
          } catch (err) {
            alert('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => {} }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      alert('Could not initiate payment. Please try again.');
    }
  };

  const handleSubmit = async () => {
    setError('');
    // Validate JD before submitting
  if (jdMode === 'paste' && !form.targetJD.trim()) {
    setError('Please paste the job description before analyzing.');
    return;
  }
  if (jdMode === 'upload' && !jdFile) {
    setError('Please upload a job description file before analyzing.');
    return;
  }
  if (!resumeFile) {
    setError('Please upload your resume before analyzing.');
    return;
  }

    try {
      const token = await getToken();
      const data = new FormData();
      data.append('currentTitle',   form.currentTitle);
      data.append('currentCompany', form.currentCompany);
      data.append('targetTitle',    form.targetTitle);
      data.append('targetCompany',  form.targetCompany);
      data.append('userEmail', user?.emailAddresses?.[0]?.emailAddress || '');
      if (jdMode === 'paste')            data.append('targetJD', form.targetJD);
      if (jdMode === 'upload' && jdFile) data.append('jdFile', jdFile);
      data.append('resume', resumeFile);

      const analyzePromise = axios.post(
        process.env.REACT_APP_API_URL + '/api/analyze',
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 600000
        }
      );

      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 1500));
      timeoutPromise.then(() => navigate('/processing'));

      const res = await analyzePromise;
      navigate('/results', { state: { results: res.data } });

    } catch (err) {
      console.error('Analysis failed:', err);

      // Plan exhausted — show upgrade modal
      if (err.response?.status === 403 && err.response?.data?.error === 'upgrade_required') {
        setShowUpgradeModal(true);

      // Queued due to Gemini overload
      } else if (err.response?.status === 503 && err.response?.data?.error === 'queued') {
        setError('⏳ ' + err.response.data.message);

      // Rate limited
      } else if (err.response?.status === 429) {
        setError('⏱️ Too many requests. Please wait a few minutes before trying again.');

      // Axios timeout
      } else if (err.code === 'ECONNABORTED') {
        setError('⌛ Analysis is taking longer than expected. Please try again — your credits have not been used.');

      // Server error
      } else if (err.response?.status === 500) {
        setError('❌ Something went wrong on our end. Please try again in a moment.');

      // Fallback
      } else {
        setError(err.response?.data?.error || '❌ Something went wrong. Please try again.');
      }
    }
  };

  const inputStyle = {
    width: '100%', padding: '0.75rem',
    background: '#141B2D', border: '1px solid #1a2640',
    borderRadius: '8px', color: 'white',
    fontSize: '1rem', boxSizing: 'border-box'
  };
  const labelStyle = {
    display: 'block', color: '#8899aa',
    marginBottom: '0.4rem', fontSize: '0.9rem'
  };

  return (
    <div style={{ background: '#0A0F1E', minHeight: '100vh', color: 'white', fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '1rem' : '2rem', boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: '560px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <span onClick={() => navigate('/')}
            style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: '1.5rem', cursor: 'pointer' }}>
            CrackPrep
          </span>
          <p style={{ color: '#8899aa', marginTop: '0.5rem', fontSize: '0.9rem' }}>Step {step} of 4</p>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '0.5rem' }}>
            {[1,2,3,4].map(s => (
              <div key={s} style={{ width: '40px', height: '4px', borderRadius: '2px', background: s <= step ? '#00D4AA' : '#1a2640' }} />
            ))}
          </div>
        </div>

        <div style={{ background: '#141B2D', borderRadius: '12px', padding: isMobile ? '1.5rem 1rem' : '2rem', border: '1px solid #1a2640' }}>

          {/* Step 1 */}
          {step === 1 && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Your Current Role</h2>
              <label style={labelStyle}>Job Title</label>
              <input style={{ ...inputStyle, marginBottom: '1rem' }} placeholder="e.g. Senior Analyst" value={form.currentTitle} onChange={e => update('currentTitle', e.target.value)} />
              <label style={labelStyle}>Company</label>
              <input style={inputStyle} placeholder="e.g. McKinsey & Company" value={form.currentCompany} onChange={e => update('currentCompany', e.target.value)} />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', gap: '0.75rem' }}>
                <button
                  onClick={() => { update('currentTitle', 'Fresher'); update('currentCompany', 'N/A'); setStep(2); }}
                  style={{ background: 'transparent', color: '#8899aa', border: '1px solid #1a2640', padding: '0.75rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                  Skip — I'm a Fresher
                </button>
                <button onClick={() => setStep(2)}
                  style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Upload Your Resume</h2>
              <div
                onClick={() => document.getElementById('resumeInput').click()}
                style={{ border: '2px dashed #00D4AA', borderRadius: '8px', padding: isMobile ? '2rem 1rem' : '2.5rem', textAlign: 'center', cursor: 'pointer', color: '#8899aa' }}>
                {resumeFile
                  ? <span style={{ color: '#00D4AA', fontSize: '0.9rem' }}>✓ {resumeFile.name}</span>
                  : <span style={{ fontSize: '0.95rem' }}>Click to upload PDF or DOCX <span style={{ color: '#8899aa', fontSize: '0.8rem' }}>(max 10MB)</span></span>}
              </div>
              <input
                id="resumeInput"
                type="file"
                accept=".pdf,.docx"
                style={{ display: 'none' }}
                onChange={handleResumeChange}
              />
            </div>
          )}

          {/* Step 3 */}
          {step === 3 && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Your Target Role</h2>
              <label style={labelStyle}>Job Title</label>
              <input style={{ ...inputStyle, marginBottom: '1rem' }} placeholder="e.g. Product Manager" value={form.targetTitle} onChange={e => update('targetTitle', e.target.value)} />
              <label style={labelStyle}>Company</label>
              <input style={inputStyle} placeholder="e.g. Google" value={form.targetCompany} onChange={e => update('targetCompany', e.target.value)} />
            </div>
          )}

          {/* Step 4 */}
          {step === 4 && (
            <div>
              <h2 style={{ marginBottom: '1.5rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>Target Job Description</h2>
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
                {['paste', 'upload'].map(mode => (
                  <button key={mode} onClick={() => setJdMode(mode)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', border: '1px solid #00D4AA', background: jdMode === mode ? '#00D4AA' : 'transparent', color: jdMode === mode ? '#0A0F1E' : '#00D4AA', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {mode === 'paste' ? 'Paste JD' : 'Upload JD'}
                  </button>
                ))}
              </div>
              {jdMode === 'paste' ? (
                <textarea
                  style={{ ...inputStyle, height: isMobile ? '150px' : '180px', resize: 'vertical' }}
                  placeholder="Paste the full job description here..."
                  value={form.targetJD}
                  onChange={e => update('targetJD', e.target.value)} />
              ) : (
                <div
                  onClick={() => document.getElementById('jdInput').click()}
                  style={{ border: '2px dashed #00D4AA', borderRadius: '8px', padding: isMobile ? '1.5rem 1rem' : '2rem', textAlign: 'center', cursor: 'pointer', color: '#8899aa' }}>
                  {jdFile
                    ? <span style={{ color: '#00D4AA', fontSize: '0.9rem' }}>✓ {jdFile.name}</span>
                    : <span style={{ fontSize: '0.9rem' }}>Click to upload PDF, DOCX, or TXT <span style={{ color: '#8899aa', fontSize: '0.8rem' }}>(max 10MB)</span></span>}
                </div>
              )}
              <input
                id="jdInput"
                type="file"
                accept=".pdf,.docx,.txt"
                style={{ display: 'none' }}
                onChange={handleJdFileChange}
              />
            </div>
          )}

          {error && <p style={{ color: '#ff6b6b', marginTop: '1rem', fontSize: '0.9rem' }}>{error}</p>}

          {/* Navigation — steps 2, 3, 4 only. Step 1 has its own nav */}
          {step > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', gap: '0.75rem' }}>
              <button onClick={() => setStep(s => s - 1)} style={{ background: 'transparent', color: '#8899aa', border: '1px solid #1a2640', padding: '0.75rem 1.25rem', borderRadius: '8px', cursor: 'pointer', flex: isMobile ? 1 : 'none' }}>Back</button>
              {step < 4
                ? <button onClick={() => setStep(s => s + 1)} style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', flex: isMobile ? 1 : 'none' }}>Next →</button>
                : <button onClick={handleSubmit} style={{ background: '#00D4AA', color: '#0A0F1E', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', flex: isMobile ? 1 : 'none' }}>Analyze Now →</button>}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            background: '#141B2D', borderRadius: '16px',
            padding: isMobile ? '1.5rem 1rem' : '2.5rem',
            maxWidth: '500px', width: '100%', border: '1px solid #1a2640',
            textAlign: 'center', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔒</div>
            <h2 style={{ color: 'white', marginBottom: '0.5rem', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
              You've used all your analyses
            </h2>
            <p style={{ color: '#8899aa', marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '0.9rem' }}>
              Purchase a plan to run more analyses. Click a plan below to pay instantly.
            </p>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { name: 'ResumePlus',    price: '₹99',  planType: 'resume_plus',    desc: '3 analyses', sub: 'ATS + Resume' },
                { name: 'InterviewPlus', price: '₹249', planType: 'interview_plus', desc: '3 analyses', sub: 'ATS + Interview' },
                { name: 'Elite',         price: '₹699', planType: 'elite',          desc: '7 analyses', sub: 'Everything' },
              ].map(plan => (
                <div
                  key={plan.planType}
                  onClick={() => handleModalPurchase(plan.planType)}
                  style={{
                    background: '#0A0F1E',
                    border: plan.name === 'Elite' ? '2px solid #00D4AA' : '1px solid #1a2640',
                    borderRadius: '10px',
                    padding: '0.875rem 0.75rem',
                    minWidth: isMobile ? '90px' : '130px',
                    cursor: 'pointer',
                    position: 'relative',
                    flex: isMobile ? 1 : 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#0d2018';
                    e.currentTarget.style.borderColor = '#00D4AA';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#0A0F1E';
                    e.currentTarget.style.borderColor = plan.name === 'Elite' ? '#00D4AA' : '#1a2640';
                  }}
                >
                  {plan.name === 'Elite' && (
                    <div style={{
                      position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)',
                      background: '#00D4AA', color: '#0A0F1E', fontSize: '0.55rem',
                      fontWeight: 'bold', padding: '0.15rem 0.5rem', borderRadius: '10px',
                      whiteSpace: 'nowrap'
                    }}>
                      BEST VALUE
                    </div>
                  )}
                  <div style={{ color: '#00D4AA', fontWeight: 'bold', fontSize: '0.8rem', marginBottom: '0.2rem' }}>{plan.name}</div>
                  <div style={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem', marginBottom: '0.1rem' }}>{plan.price}</div>
                  <div style={{ color: '#8899aa', fontSize: '0.7rem', marginBottom: '0.1rem' }}>{plan.desc}</div>
                  <div style={{ color: '#8899aa', fontSize: '0.65rem', marginBottom: '0.4rem' }}>{plan.sub}</div>
                  <div style={{ color: '#00D4AA', fontSize: '0.75rem', fontWeight: 'bold' }}>Buy Now →</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setShowUpgradeModal(false); navigate('/pricing'); }}
                style={{ background: 'transparent', color: '#00D4AA', border: '1px solid #00D4AA', padding: '0.6rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                View All Plans
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                style={{ background: 'transparent', color: '#8899aa', border: '1px solid #1a2640', padding: '0.6rem 1.25rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}